'use client'

import { publicEventPrice } from './publicEventPrice'
import type { PublicEventPrice } from './publicEventPrice'
import { formatPriceMinor } from '../../../lib/formatPriceMinor'
import { EventPriceRow } from './EventPriceRow'
import { PublicScheduleEntryPromotions } from './PublicScheduleEntryPrice'
import {PublicAcceptedPasses} from './PublicAcceptedPasses'
import {PublicBookingRules} from './PublicBookingRules'
import { PublicOfferDetailsTrigger } from './PublicOfferDetailsTrigger'
import { useState, type CSSProperties } from 'react'
import Link from 'next/link'
import { CreditCard, MapPin, UserRound, UsersRound } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import { useAuth } from '@fitro/customer-host/auth'
import { cn } from '../../../lib/utils'
import { buildAuthHref } from './publicEntryLinks'
import {
    getAuthenticatedEventDetailsHref,
    openTermFromStudioPage,
    getPublicEventBookingMode,
    getPublicEventDetailsHref,
} from './publicEventLinks'
import { PublicGuestBookingChooser } from './PublicGuestBookingChooser'
import { getPublicEventGuestCheckoutHref } from './publicEventLinks'
import { PublicTermDetails, usePublicTermSession } from './PublicTermDetails'
import { publicScheduleStyles } from './publicEntryStyles'
import { Avatar } from '../../ui/Avatar'
import type { PublicScheduleEvent } from '../../../services/public-schedule.service'

export type PublicScheduleAvailabilityStatus = 'available' | 'last_spots' | 'full' | 'registration_closed' | 'occurred'
export type PublicScheduleCardVariant = 'grid' | 'list'

export type PublicScheduleCardEvent = {
    id: string
    accentColor: string
    availabilityStatus: PublicScheduleAvailabilityStatus
    className: string
    dateLabel: string
    instructor: string | null
    instructorAvatars: Array<{ name: string; src: string | null }>
    location: string | null
    sourceEvent: PublicScheduleEvent
    startTimeLabel: string
    endTimeLabel: string
    /** The day a Term ends on when it is not the day it starts on. */
    endDateLabel?: string | null
}

const AVAILABILITY_LABEL_KEYS: Record<
    PublicScheduleAvailabilityStatus,
    | 'publicEntry.schedule.availability.available'
    | 'publicEntry.schedule.availability.lastSpots'
    | 'publicEntry.schedule.availability.full'
    | 'publicEntry.schedule.availability.registrationClosed'
    | 'publicEntry.schedule.availability.occurred'
> = {
    available: 'publicEntry.schedule.availability.available',
    last_spots: 'publicEntry.schedule.availability.lastSpots',
    full: 'publicEntry.schedule.availability.full',
    registration_closed: 'publicEntry.schedule.availability.registrationClosed',
    occurred: 'publicEntry.schedule.availability.occurred',
}

// Long room or instructor names wrap to two lines, then end with an ellipsis; the full name is in the title.
const COMPACT_EVENT_TEXT_CLASS_NAME = 'line-clamp-2 [overflow-wrap:anywhere]'
const COMPACT_EVENT_TITLE_CLASS_NAME = 'line-clamp-2'

const dateNote = (label: string) => (
    <span className="inline-block whitespace-nowrap text-[12px] font-normal text-[var(--landing-neutral-400)]">({label})</span>
)
export function PublicScheduleEventCard({
    event,
    variant,
    showBookingSummary = true,
}: {
    event: PublicScheduleCardEvent
    variant: PublicScheduleCardVariant
    showBookingSummary?: boolean
}) {
    const { t } = useTranslation('landing')
    const { isLoaded, isSignedIn } = useAuth()
    const compact = variant === 'grid'
    const actionSizeClassName = compact ? 'min-h-[34px] px-2 text-[12px]' : 'min-h-11 px-4 text-sm'
    const occurred = event.availabilityStatus === 'occurred'
    const eventAccentStyle = { '--public-event-accent': event.accentColor } as CSSProperties
    const hasInstructor = Boolean(event.instructor)
    const hasLocation = Boolean(event.location)
    const hasDetailsBeforeAvailability = hasInstructor || hasLocation
    const showAvailability = showBookingSummary || !['available', 'last_spots'].includes(event.availabilityStatus)
    const showDateLabel = !compact && event.dateLabel
    const entryPrice = occurred || !showBookingSummary ? null : publicEventPrice(event.sourceEvent)
    const cardPrices = event.sourceEvent.benefitCardPrices ?? []
    const eventHref = getAuthenticatedEventDetailsHref(event.sourceEvent)
    const signedIn = Boolean(isLoaded && isSignedIn)
    const signInHref = buildAuthHref('/sign-in', eventHref)
    const oneOffDetailsHref =
        event.sourceEvent.isCyclical === false ? getPublicEventDetailsHref(event.sourceEvent) : null
    const bookingHref = signedIn ? eventHref : (oneOffDetailsHref ?? signInHref)
    const bookingMode = getPublicEventBookingMode(
        { ...event.sourceEvent, availabilityStatus: event.availabilityStatus },
        signedIn,
    )
    const reserveLabel = t('publicEntry.cta.reserveEvent', {
        className: event.className,
        date: event.dateLabel,
        time: event.startTimeLabel,
    })
    // The same term line as the booking window and the class page.
    const sessionLabel = usePublicTermSession(event.sourceEvent).label
    const showAccentRail = event.sourceEvent.isCyclical
    const availabilityLabel = t(AVAILABILITY_LABEL_KEYS[event.availabilityStatus])
    // The details window hands joining the waitlist to the card's own window, so only one window is open.
    const [waitlistRequest, setWaitlistRequest] = useState(0)
    const renderAction = (sizeClassName: string) => {
        const actionClassName = cn(publicScheduleStyles.actionBase, sizeClassName, publicScheduleStyles.actionPrimary)
        // A guest who chose to book goes straight to the form on the Term page.
        if (bookingMode === 'guest') {
            return (
                <Link href={getPublicEventGuestCheckoutHref(event.sourceEvent) ?? signInHref} aria-label={reserveLabel} className={actionClassName}>
                    {t('publicEntry.cta.reserve')}
                </Link>
            )
        }
        if (bookingMode === 'waitlist') {
            return (
                <PublicGuestBookingChooser
                    className={actionClassName}
                    event={event.sourceEvent}
                    mode="waitlist"
                    openRequest={waitlistRequest}
                    signInHref={signInHref}
                >
                    {t('publicEntry.guestCheckout.waitlist')}
                </PublicGuestBookingChooser>
            )
        }
        if (bookingMode === 'account') {
            // Signed in, one full load opens the Term in the app; a client transition would leave the studio's page behind it.
            return signedIn ? (
                <a href={bookingHref} aria-label={reserveLabel} className={actionClassName} onClick={(click) => openTermFromStudioPage(click, event.sourceEvent)}>
                    {t('publicEntry.cta.reserve')}
                </a>
            ) : (
                <Link href={bookingHref} aria-label={reserveLabel} className={actionClassName}>
                    {t('publicEntry.cta.reserve')}
                </Link>
            )
        }
        return null
    }
    const unavailableClassName = cn(
        publicScheduleStyles.actionBase,
        'pointer-events-none mt-auto w-full cursor-not-allowed border border-[var(--landing-neutral-100)] bg-[rgba(237,240,253,0.72)] text-[var(--landing-neutral-400)]',
    )
    const titleClassName = cn(
        'btn-micro -mx-1 block w-fit max-w-full rounded-[4px] px-1 text-left underline decoration-current/30 decoration-1 underline-offset-[3px] transition-[text-decoration-color] hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        occurred ? 'text-slate-500' : 'text-[var(--landing-primary-600)]',
        // The clamp belongs to the element holding the text; a long word wraps instead of being cut.
        compact && `${COMPACT_EVENT_TITLE_CLASS_NAME} break-words`,
    )
    const detailsActionClassName = 'min-h-11 px-6 text-sm sm:w-auto'
    const detailsAction = (close: () => void) => bookingMode === 'waitlist' ? (
        <button
            type="button"
            className={cn(publicScheduleStyles.actionBase, detailsActionClassName, publicScheduleStyles.actionPrimary)}
            onClick={() => {
                close()
                setWaitlistRequest(request => request + 1)
            }}
        >
            {t('publicEntry.guestCheckout.waitlist')}
        </button>
    ) : renderAction(detailsActionClassName)
    const detailsFooter = (close: () => void) => bookingMode ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {/* Open spots go without saying next to the booking action; only scarcity is worth a line. */}
            {showAvailability && event.availabilityStatus !== 'available' ? (
                <p className={cn(
                    'text-center text-sm font-medium sm:text-left',
                    event.availabilityStatus === 'last_spots' ? 'text-amber-700' : 'text-[var(--landing-neutral-500)]',
                )}>
                    {availabilityLabel}
                </p>
            ) : <span aria-hidden="true"/>}
            {detailsAction(close)}
        </div>
    ) : (
        <span aria-disabled="true" className={cn(unavailableClassName, 'min-h-11 px-4 text-sm')}>
            {availabilityLabel}
        </span>
    )

    return (
        <article
            className={`relative flex flex-col overflow-hidden rounded-[10px] border ${occurred ? 'border-slate-100 bg-slate-50 shadow-none' : 'border-[var(--landing-neutral-100)] bg-white shadow-[0_10px_24px_rgba(57,98,198,0.06)]'} ${compact ? `gap-2 px-3 py-2 ${showAccentRail ? 'pl-[22px]' : ''}` : `gap-4 px-4 py-4 ${showAccentRail ? 'pl-7' : ''}`}`}
            style={eventAccentStyle}
        >
            {showAccentRail ? (
                <div
                    className={`absolute bottom-2 left-[7px] top-2 w-1.5 rounded-full ${occurred ? 'bg-slate-200' : 'bg-[var(--public-event-accent)]'}`}
                />
            ) : null}

            <div className="space-y-1">
                <p
                    className={`${compact ? 'text-[12px]' : 'text-[14px]'} leading-[1.2] ${occurred ? 'text-slate-400' : 'text-[var(--landing-neutral-400)]'}`}
                >
                    <span
                        className={`inline-block whitespace-nowrap font-bold ${occurred ? 'text-slate-500' : 'text-[var(--landing-neutral-700)]'}`}
                    >
                        {t('publicEntry.schedule.timeFrom', { time: event.startTimeLabel })}
                    </span>
                    {/* A Term ending another day names both days, each next to its hour. */}
                    {showDateLabel && event.endDateLabel ? <>{' '}{dateNote(event.dateLabel)}</> : null}{' '}
                    <span className="inline-block whitespace-nowrap">
                        {t('publicEntry.schedule.timeTo', { time: event.endTimeLabel })}
                    </span>
                    {event.endDateLabel ? <>{' '}{dateNote(event.endDateLabel)}</> : showDateLabel ? <>{' '}{dateNote(event.dateLabel)}</> : null}
                </p>
                <h4
                    className={`${compact ? 'text-[15px]' : 'text-[18px] [overflow-wrap:anywhere]'} font-bold leading-[1.2] tracking-normal`}
                    title={event.className}
                >
                    {oneOffDetailsHref ? (
                        <Link href={oneOffDetailsHref} className={titleClassName}>
                            {event.className}
                        </Link>
                    ) : (
                        <PublicOfferDetailsTrigger
                            name={event.className}
                            description={event.sourceEvent.description}
                            fullDescriptionHtml={event.sourceEvent.fullDescriptionHtml}
                            subtitle={sessionLabel}
                            className={titleClassName}
                            summary={<PublicTermDetails event={event.sourceEvent} muted={!bookingMode}/>}
                            footer={detailsFooter}
                        >
                            <PublicScheduleEntryPromotions key={`promotions:${event.sourceEvent.id}`} event={event.sourceEvent}/>
                            <PublicAcceptedPasses key={event.sourceEvent.id} event={event.sourceEvent}/>
                            <PublicBookingRules key={`rules:${event.sourceEvent.id}`} event={event.sourceEvent} inline/>
                        </PublicOfferDetailsTrigger>
                    )}
                </h4>
            </div>

            <div className="space-y-2">
                {event.instructor ? (
                    <InstructorMetaRow
                        avatars={event.instructorAvatars}
                        compact={compact}
                        label={event.instructor}
                        muted={occurred}
                    />
                ) : null}
                {event.location ? (
                    <>
                        {hasInstructor ? <EventMetaDivider muted={occurred} /> : null}
                        <EventMetaRow icon="location" label={event.location} compact={compact} muted={occurred} />
                    </>
                ) : null}
                {showAvailability && hasDetailsBeforeAvailability ? <EventMetaDivider muted={occurred} /> : null}
                {showAvailability ? (
                    <EventMetaRow
                        icon="availability"
                        label={availabilityLabel}
                        compact={compact}
                        muted={occurred}
                        wrap={compact}
                    />
                ) : null}
                {entryPrice ? (
                    <>
                        <EventMetaDivider />
                        <LandingEventPriceRow price={entryPrice} compact={compact} />
                    </>
                ) : null}
                {entryPrice?.kind === 'paid' && cardPrices.length > 0 ? (
                    <>
                        <EventMetaDivider />
                        <EventCardPriceRow cardPrices={cardPrices} currency={entryPrice.currency} compact={compact} />
                    </>
                ) : null}
            </div>

            {renderAction(actionSizeClassName) ?? (event.availabilityStatus === 'full' ? (
                <span aria-disabled="true" className={cn(unavailableClassName, actionSizeClassName)}>
                    {t('publicEntry.schedule.availability.full')}
                </span>
            ) : null)}
        </article>
    )
}

export function PublicScheduleEmptyState({
    className = '',
    scope = 'week',
}: {
    className?: string
    scope?: 'day' | 'week'
}) {
    const { t } = useTranslation('landing')
    return (
        <div className={cn(publicScheduleStyles.emptyState, className)}>
            <h3 className={publicScheduleStyles.emptyTitle}>
                {t(scope === 'day' ? 'publicEntry.schedule.empty.dayTitle' : 'publicEntry.schedule.empty.title')}
            </h3>
            <p className={publicScheduleStyles.emptyBody}>{t('publicEntry.schedule.empty.body')}</p>
        </div>
    )
}

function LandingEventPriceRow({ price, compact }: { price: PublicEventPrice; compact: boolean }) {
    const { t, locale } = useTranslation('landing')
    const { t: tClient } = useTranslation()
    return (
        <EventPriceRow
            price={price}
            compact={compact}
            locale={locale}
            labels={{
                free: t('publicEntry.events.free'),
                passesOnly: t('publicEntry.events.passesOnly'),
                lowest30d: lowest => tClient('client.price.lowest30d', { price: lowest }),
                firstEntry: amount => t('publicEntry.events.firstEntry', { amount }),
            }}
        />
    )
}

/** A card price is its own price for card holders, never a reduction (ADR 0015). */
function EventCardPriceRow({
    cardPrices,
    currency,
    compact,
}: {
    cardPrices: NonNullable<PublicScheduleEvent['benefitCardPrices']>
    currency: string
    compact: boolean
}) {
    const { t, locale } = useTranslation('landing')
    return (
        <div className="flex min-w-0 items-start gap-2 pl-1">
            <CreditCard
                aria-hidden="true"
                className={`${compact ? 'h-[17px] w-[17px]' : 'h-[18px] w-[18px]'} shrink-0 text-[var(--landing-primary-500)]`}
                strokeWidth={2.25}
            />
            <div
                className={`min-w-0 flex-1 divide-y divide-dashed divide-[var(--landing-neutral-100)] leading-[1.2] text-[var(--landing-neutral-500)] ${compact ? 'text-[12px]' : 'text-[14px]'}`}
            >
                {cardPrices.map(({ cardName, priceMinor }) => (
                    <p key={cardName} className="break-words py-1 first:pt-0 last:pb-0">
                        {cardName}:{' '}
                        <span className="whitespace-nowrap tabular-nums">
                            {priceMinor === 0
                                ? t('publicEntry.schedule.cardPriceFree')
                                : formatPriceMinor(priceMinor, currency, locale)}
                        </span>
                    </p>
                ))}
            </div>
        </div>
    )
}

function EventMetaDivider({ muted = false }: { muted?: boolean }) {
    return <div className={`h-px w-full ${muted ? 'bg-slate-200' : 'bg-[var(--landing-neutral-100)]'}`} />
}

function InstructorMetaRow({
    avatars,
    compact,
    label,
    muted = false,
}: {
    avatars: PublicScheduleCardEvent['instructorAvatars']
    compact: boolean
    label: string
    muted?: boolean
}) {
    return (
        <div className="flex min-w-0 items-center gap-2 pl-1">
            {avatars.length > 0 ? (
                <span className="flex shrink-0 -space-x-1" aria-hidden="true">
                    {/* Two photos at most, so the names keep their room in a narrow column. */}
                    {avatars.slice(0, 2).map((avatar) => (
                        <Avatar
                            key={`${avatar.name}-${avatar.src}`}
                            name={avatar.name}
                            src={avatar.src}
                            size="small"
                            textClassName="text-[7px]"
                            className={`${compact ? 'h-[17px] w-[17px]' : 'h-[18px] w-[18px]'} ring-1 ring-white`}
                        />
                    ))}
                </span>
            ) : (
                <UserRound
                    aria-hidden="true"
                    className={`${compact ? 'h-[17px] w-[17px]' : 'h-[18px] w-[18px]'} shrink-0 ${muted ? 'text-slate-400' : 'text-[var(--landing-primary-500)]'}`}
                    strokeWidth={2.25}
                />
            )}
            <p
                className={`${compact ? `text-[12px] ${COMPACT_EVENT_TEXT_CLASS_NAME}` : 'text-[14px]'} min-w-0 leading-[1.2] ${muted ? 'text-slate-500' : 'text-[var(--landing-neutral-500)]'} ${compact ? 'break-words' : 'truncate'}`}
                title={label}
            >
                {label}
            </p>
        </div>
    )
}

function EventMetaRow({
    compact,
    icon,
    label,
    muted = false,
    wrap = false,
}: {
    compact: boolean
    icon: 'instructor' | 'location' | 'availability'
    label: string
    muted?: boolean
    wrap?: boolean
}) {
    const Icon = icon === 'location' ? MapPin : icon === 'availability' ? UsersRound : UserRound
    const shouldWrap = wrap || compact

    return (
        <div className="flex min-w-0 items-start gap-2 pl-1">
            <Icon
                aria-hidden="true"
                className={`${compact ? 'h-[17px] w-[17px]' : 'h-[18px] w-[18px]'} shrink-0 ${muted ? 'text-slate-400' : 'text-[var(--landing-primary-500)]'}`}
                strokeWidth={2.25}
            />
            <p
                className={`${compact ? `text-[12px] ${COMPACT_EVENT_TEXT_CLASS_NAME}` : 'text-[14px]'} min-w-0 leading-[1.2] ${muted ? 'text-slate-500' : 'text-[var(--landing-neutral-500)]'} ${shouldWrap ? 'break-words' : 'truncate'}`}
                title={label}
            >
                {label}
            </p>
        </div>
    )
}
